// @ts-strict-ignore
import moment from 'moment';

interface StoredValue {
  value: any;
  expiration?: string | null;
}

function parseValue(value) {
  try {
    return JSON.parse(value);
  } catch (e) {
    return value;
  }
}

/**
 * Sets a value in localStorage with an optional expiration time.
 * @param key The key to store the value under.
 * @param value The value to store.
 * @param expirationInHours Optional. The number of hours until the value expires.
 */
function setLocalStorageValue(key: string, value: string, expirationInHours?: number): void {
  if (window.localStorage) {
    const expiration = expirationInHours ? moment().add(expirationInHours, 'hours').toISOString() : null;

    const storedValue: StoredValue = {
      value,
      expiration,
    };

    window.localStorage.setItem(key, JSON.stringify(storedValue));
  }
}

/**
 * Gets a value from localStorage. If the value has expired, it is removed and null is returned.
 * Supports both string and object values for backward compatibility.
 * @param key The key to retrieve the value from.
 * @returns The stored value or null if it has expired or doesn't exist.
 */
function getLocalStorageValue(key: string): string | null {
  if (window.localStorage) {
    const storedValue = window.localStorage.getItem(key);
    const parsedValue = parseValue(storedValue);
    if (!parsedValue) {
      return null;
    }

    if (typeof parsedValue === 'string') {
      return parsedValue;
    }

    if (parsedValue.expiration && moment().isAfter(parsedValue.expiration)) {
      window.localStorage.removeItem(key);
      return null;
    }

    return parsedValue.value;
  }
  return null;
}

function removeLocalStorageValue(key: string): void {
  if (window.localStorage) {
    window.localStorage.removeItem(key);
  }
}

export {
  setLocalStorageValue,
  getLocalStorageValue,
  removeLocalStorageValue,
};
